// Responsive workspace shell, "Paper and Line" (UX spec 3 and 4): the masthead (header) with the
// typographic index (nav), the paper workspace (main), the graphite Line down the right edge
// (the phone dock, aside) and the status bar along the bottom (footer), which together frame the
// page. Landmarks and F6 region cycling per UX spec 4.1; the command palette (Ctrl/Cmd+K, 6.1) and
// the shortcut help ("?", 10) open over any page.
import type { Agent } from '@bcio-voice/contracts';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, ChevronDown, Keyboard, LogOut, PhoneCall, Search, Settings } from 'lucide-react';
import { ViewAsBanner, ViewAsMenuSection } from './ViewAsBanner.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Keys } from '../ui/Keys.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { ShortcutHelp } from './ShortcutHelp.tsx';
import { AVAILABILITY } from './availability.ts';
import { useApplyDensity } from '../state/density.ts';
import { type Platform, detectPlatform, findShortcut, shellShortcut, shortcutText, singleKeyShortcutsOn } from '../lib/shortcuts.ts';
import { Link, routeTitle, useRouter } from '../lib/router.tsx';
import { useWorkspace } from '../state/workspace.tsx';
import { ownPhoneStatus } from '../call/gating.ts';
import { capability, gate, hasPermission } from '../lib/capabilities.ts';
import { canSwitchTenant } from '../state/session.ts';
import { windowTitle } from '../config/brand.ts';
import { Wordmark } from '../ui/Wordmark.tsx';
import { initials } from '../lib/format.ts';
import { DesktopHooks } from '../desktop/DesktopHooks.tsx';
import { BottomNav, Index, MorePage, type useNav } from './nav.tsx';
import { featureNotice, gateDiagnostic, gateShortLabel } from '../lib/userCopy.ts';
import { Diagnostics } from '../ui/Diagnostics.tsx';
import {RecordingNotice} from '../features/phone/RecordingNotice.tsx';
import { GuidedTour } from '../features/tutorial/GuidedTour.tsx';
import { armCallWindowAnswer } from '../features/phone/CallWindowDock.tsx';
import { PhoneDock } from '../features/phone/PhoneDock.tsx';
import { IncomingCallBanner } from '../features/phone/IncomingCallBanner.tsx';
import { CallLinkPage } from '../call/CallLinkPage.tsx';
import { PhonePermissionBanner } from '../features/phone/MicrophoneHelp.tsx';
import { CallsPage } from '../features/inbox/CallsPage.tsx';
import { ContactsPage } from '../features/contacts/ContactsPage.tsx';
import { TeamPage } from '../features/team/TeamPage.tsx';
import { SettingsPage } from '../features/settings/SettingsPage.tsx';
import { MessagingPage } from '../features/messaging/index.ts';
import { NotificationsPage } from '../features/settings/notifications/index.ts';
import { AdminMount, AnalyticsMount, BlocklistMount, FlowMount, RoutingMount, VoicemailMount } from './featureModules.tsx';
import { CoachingMonitorPage } from '../features/coaching/MonitorPage.tsx';
import { StatePanel } from '../ui/StatePanel.tsx';
import { formatClock, formatDuration } from '../lib/format.ts';
import { partyName, readinessTone } from '../features/phone/callView.ts';
import { isIncomingFor } from '../call/callStore.ts';
import type { ErrorView } from '../lib/errorCopy.ts';
import { RefreshBar } from '../ui/RefreshBar.tsx';
import { useBackgroundRefresh } from '../state/refreshActivity.ts';

export interface ShellProps {
  expiredBanner: ReactNode | null;
  onSwitchTenant: (tenantId: string) => Promise<ErrorView | null>;
  onSignOut: () => void;
}

export function Shell({ expiredBanner, onSwitchTenant, onSignOut }: ShellProps) {
  const ws = useWorkspace();
  const { route, navigate, search } = useRouter();
  const incoming = ws.liveCalls.find(c => isIncomingFor(c, ws.userId)) ?? null;
  const live = incoming ?? ws.liveCalls[0] ?? null;
  useWindowTitle(routeTitle(route), live, incoming ? partyName(incoming, ws.userId, ws.userName) : null);
  useRegionCycling();
  useApplyDensity();
  const overlays = useOverlays();
  const fixture = ws.config.value?.data_mode === 'fixture';
  const onPhoneRoute = route.name === 'phone' || route.name === 'call_link';

  return (
    <div className={`shell${live ? ' in-call' : ''}${incoming ? ' has-incoming' : ''}${onPhoneRoute ? ' phone-route' : ''}${route.name === 'call_link' ? ' call-link-route' : ''}`}>
      <a className="skip-link" href="#main">Skip to main content</a>
      <a className="skip-link" href="#phone">Skip to phone</a>
      {fixture ? <div className="fixture-banner" role="note"><span className="fixture-label">Fixture data: not real calls</span></div> : null}
      {expiredBanner}
      <ViewAsBanner />
      <TopBar onSwitchTenant={onSwitchTenant} onSignOut={onSignOut} onOpenPalette={overlays.openPalette} onOpenShortcuts={overlays.openHelp} platform={overlays.platform} />
      <div className="workspace-notices"><RecordingNotice /><PhonePermissionBanner /></div>
      {incoming ? <IncomingCallBanner /> : live && !onPhoneRoute ? <CallBar /> : null}
      <main id="main" className="workspace" tabIndex={-1}>
        <RouteView />
      </main>
      <PhoneDock />
      <DesktopHooks />
      <StatusBar />
      <BottomNav />
      <CommandPalette open={overlays.palette} onClose={overlays.closePalette} onShowShortcuts={overlays.openHelp} />
      <ShortcutHelp open={overlays.help} onClose={overlays.closeHelp} platform={overlays.platform} />
      <GuidedTour openRequested={search.get('tour') === 'amy'} navigate={navigate} liveCall={Boolean(live)} readOnly={Boolean(ws.session.view_as)} />
    </div>
  );
}

/**
 * The command palette (Ctrl/Cmd+K, a toggle) and the shortcut help ("?" when not typing, if
 * single-key shortcuts are on). Neither opens over another open dialog, such as sign-in again.
 */
function useOverlays() {
  const [palette, setPalette] = useState(false);
  const [help, setHelp] = useState(false);
  const platform = useMemo<Platform>(() => detectPlatform(), []);
  const state = useRef({ palette, help });
  state.current = { palette, help };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const hit = shellShortcut(event, singleKeyShortcutsOn());
      if (!hit) return;
      const { palette: paletteOpen, help: helpOpen } = state.current;
      const otherDialog = [...document.querySelectorAll('dialog[open]')].some(d => !d.classList.contains('palette'));
      if (hit === 'palette') {
        if (otherDialog) return;
        event.preventDefault();
        setPalette(!paletteOpen);
      } else if (!paletteOpen && !helpOpen && !otherDialog) {
        event.preventDefault();
        setHelp(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return {
    palette, help, platform,
    openPalette: () => { setHelp(false); setPalette(true); },
    closePalette: () => setPalette(false),
    openHelp: () => { setPalette(false); setHelp(true); },
    closeHelp: () => setHelp(false),
  };
}

/** Workspace setup (admin, routing, phone menus) as one disclosure, so the index stays one line. */
function SetupMenu({ items }: { items: ReturnType<typeof useNav> }) {
  const { route } = useRouter();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const current = items.find(item => item.match.includes(route.name)) ?? null;
  useEffect(() => setOpen(false), [route.name]);
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('focusin', away);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('focusin', away); };
  }, [open]);
  return (
    <div className="index-setup" ref={wrap} onKeyDown={e => { if (e.key === 'Escape' && open) { setOpen(false); button.current?.focus(); } }}>
      <button ref={button} type="button" className={`index-link index-button${current ? ' is-current' : ''}`} aria-expanded={open} aria-controls="index-setup-menu" onClick={() => setOpen(o => !o)}>
        Setup{current ? <><span className="sep" aria-hidden="true" /><span className="index-current">{current.label}</span></> : null} <Icon icon={ChevronDown} size="1em" />
      </button>
      {open ? (
        <ul id="index-setup-menu" className="popover index-menu">
          {items.map(item => (
            <li key={item.to}>
              <Link to={item.to} className="menu-item" aria-current={item.match.includes(route.name) ? 'page' : undefined}><Icon icon={item.icon} /> {item.label}</Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function RouteView() {
  const ws = useWorkspace();
  const { route } = useRouter();
  const focusRef = useRef(route.name);
  useEffect(() => {
    // Move focus to the page heading on navigation so screen readers hear the new page.
    if (focusRef.current === route.name) return;
    focusRef.current = route.name;
    const h1 = document.querySelector<HTMLElement>('#main h1');
    if (h1) { h1.tabIndex = -1; h1.focus({ preventScroll: false }); }
  }, [route.name]);
  const featureProps = { api: ws.api, session: ws.session, config: ws.config.value };
  switch (route.name) {
    case 'calls':
      return <CallsPage callId={route.callId} />;
    case 'contacts':
      return <ContactsPage contactId={route.contactId} creating={route.creating} />;
    case 'messages':
      return <MessagingPage threadId={route.threadId} />;
    case 'team':
      return <TeamPage />;
    case 'analytics':
      return <AnalyticsMount {...featureProps} />;
    case 'live_call':
      return <CoachingMonitorPage callId={route.callId} />;
    case 'admin':
      return <AdminMount {...featureProps} />;
    case 'routing':
      return <RoutingMount {...featureProps} />;
    case 'flow':
      return <FlowMount {...featureProps} />;
    case 'blocklist':
      return <BlocklistMount {...featureProps} />;
    case 'voicemail':
      return <VoicemailMount {...featureProps} />;
    case 'settings':
      return route.section === 'notifications' ? <NotificationsPage /> : <SettingsPage />;
    case 'more':
      return <MorePage />;
    case 'phone':
    case 'call_link':
      if (route.name === 'call_link') return <CallLinkPage />;
      return <div className="page phone-page"><h1 className="page-title visually-hidden">Phone</h1><p className="field-hint">The phone is below on small screens and beside your work on wider screens.</p></div>;
    default:
      return (
        <div className="page">
          <h1 className="page-title">Not found</h1>
          <StatePanel variant="empty" title="This page doesn't exist" body="Check the address, or go back to your calls." action={<Link to="/inbox/calls" className="btn">Go to calls</Link>} />
        </div>
      );
  }
}

function TopBar({ onSwitchTenant, onSignOut, onOpenPalette, onOpenShortcuts, platform }: Pick<ShellProps, 'onSwitchTenant' | 'onSignOut'> & { onOpenPalette: () => void; onOpenShortcuts: () => void; platform: Platform }) {
  const ws = useWorkspace();
  const paletteKeys = findShortcut('palette')?.keys ?? [];
  const [menu, setMenu] = useState(false);
  const accountButton = useRef<HTMLButtonElement>(null);
  // Menu items close the menu; focus returns to the account button before an overlay opens, so the
  // overlay hands it back there.
  const fromMenu = (open: () => void) => () => { setMenu(false); accountButton.current?.focus(); open(); };
  const [switchError, setSwitchError] = useState<string | null>(null);
  const switching = canSwitchTenant(ws.session, ws.liveCalls.length > 0);
  return (
    <header className="topbar masthead">
      <div className="topbar-left">
        <span className="topbar-brand"><Wordmark /></span>
        {ws.session.memberships.length > 1 ? (
          <label className="tenant-switch">
            <span className="visually-hidden">Workspace</span>
            <select
              value={ws.session.tenant?.id ?? ''}
              disabled={!switching.allowed}
              title={switching.reason ?? undefined}
              aria-describedby={switching.reason ? 'tenant-why' : undefined}
              onChange={e => { const id = e.target.value; setSwitchError(null); void onSwitchTenant(id).then(err => err && setSwitchError(err.title)); }}
            >
              {ws.session.memberships.map(m => <option key={m.tenant_id} value={m.tenant_id}>{m.name}</option>)}
            </select>
            {switching.reason ? <span id="tenant-why" className="visually-hidden">{switching.reason}</span> : null}
          </label>
        ) : (
          <span className="tenant-name">{ws.session.tenant?.name}</span>
        )}
        {switchError ? <span className="field-error" role="alert">{switchError}</span> : null}
      </div>
      <Index renderSetup={setup => <SetupMenu items={setup} />} />
      <div className="topbar-right">
        <button type="button" className="btn btn-small palette-trigger" onClick={onOpenPalette} aria-keyshortcuts={platform === 'mac' ? 'Meta+K' : 'Control+K'} title={`Search and commands (${shortcutText(paletteKeys, platform)})`}>
          <Icon icon={Search} size="1em" /> Search <Keys keys={paletteKeys} platform={platform} />
        </button>
        <AvailabilityControl />
        <div className="account">
          <button ref={accountButton} type="button" className="btn btn-quiet account-btn" aria-label={`Account menu for ${ws.session.user?.display_name ?? 'you'}`} aria-expanded={menu} aria-controls="account-menu" onClick={() => setMenu(m => !m)}>
            <span className="avatar avatar-small" aria-hidden="true">{initials(ws.session.user?.display_name)}</span>
            <span className="account-name">{ws.session.user?.display_name}</span> <Icon icon={ChevronDown} size="1em" />
          </button>
          {menu ? (
            <div id="account-menu" className="popover account-menu" onKeyDown={e => { if (e.key === 'Escape') setMenu(false); }}>
              <p className="meta">{ws.session.user?.email}</p>
              <Link to="/settings" className="menu-item" onClick={() => setMenu(false)}><Icon icon={Settings} /> Settings</Link>
              <Link to="/phone?tour=amy" className="menu-item" onClick={() => setMenu(false)}><Icon icon={BookOpen} /> Guided tour</Link>
              <button type="button" className="menu-item" onClick={fromMenu(onOpenPalette)}><Icon icon={Search} /> Search and commands</button>
              <button type="button" className="menu-item" onClick={fromMenu(onOpenShortcuts)}><Icon icon={Keyboard} /> Keyboard shortcuts</button>
              <ViewAsMenuSection onDone={() => setMenu(false)} />
              <button type="button" className="menu-item" onClick={() => { setMenu(false); onSignOut(); }}><Icon icon={LogOut} /> Sign out</button>
            </div>
          ) : null}
        </div>
      </div>
      <RefreshBar />
    </header>
  );
}

export function AvailabilityControl() {
  const ws = useWorkspace();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canWrite = hasPermission(ws.permissions, 'presence:write_own') && !ws.session.view_as;
  if (!ws.me) {
    return ws.agents.loading ? <span className="chip">Status loading…</span> : <span className="chip" title={ws.agents.error?.title}>Status unavailable</span>;
  }
  const me = ws.me;
  const phoneStatus = ownPhoneStatus(me, ws.readiness, ws.phoneOwnership, {
    viewAs: Boolean(ws.session.view_as),
    expectedAccount: `${ws.session.tenant?.id ?? ''}:${ws.userId}`,
    deviceStatus: ws.deviceStatus,
  });
  return (
    <div className="availability">
      <label>
        <span className="visually-hidden">Your status</span>
        <span className={`dot dot-${me.availability === 'available' ? 'live' : me.availability === 'offline' ? 'neutral' : me.availability === 'do_not_disturb' ? 'end' : 'warn'}`} aria-hidden="true" />
        <select value={me.availability} disabled={!canWrite || busy} aria-busy={busy || undefined} aria-describedby={phoneStatus ? 'avail-warn' : undefined}
          onChange={async e => {
            if (!canWrite) return;
            setBusy(true);
            setError(null);
            const err = await ws.setAvailability(e.target.value as Agent['availability']);
            setBusy(false);
            if (err) setError(err.title);
          }}>
          {AVAILABILITY.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </label>
      {me.derived_call_state === 'busy' ? <span className="chip tone-live"><span className="dot dot-live" aria-hidden="true" /> On a call</span> : null}
      {phoneStatus ? <span id="avail-warn" className={`chip ${phoneStatus.tone === 'warn' ? 'tone-warn ' : ''}avail-warn`}><span className={`dot dot-${phoneStatus.tone === 'warn' ? 'warn' : 'live'}`} aria-hidden="true" /> <span className="avail-warn-text">{phoneStatus.text}</span></span> : null}
      {error ? <span className="field-error" role="alert">{error}</span> : null}
    </div>
  );
}

/** Compact call bar on small screens and on pages where the dock is hidden. */
function CallBar() {
  const ws = useWorkspace();
  const incomingCall = ws.liveCalls.find(c => isIncomingFor(c, ws.userId)) ?? null;
  const call = incomingCall ?? ws.liveCalls[0];
  if (!call) return null;
  const name = partyName(call, ws.userId, ws.userName);
  const incoming = isIncomingFor(call, ws.userId);
  const lamp = incoming ? 'ring' : call.state === 'held' ? 'hold' : call.state === 'active' ? 'live' : 'neutral';
  const engineCall = ws.callControl.engineCall(call);
  const pending = ws.callControl.pending(call);
  const canAnswer = incoming && ws.readiness.state === 'ready' && !!engineCall && engineCall.phase === 'offered' && !pending && call.allowed_actions.includes('answer');
  const canDecline = incoming && !pending && (call.allowed_actions.includes('reject') || call.allowed_actions.includes('hangup'));
  return (
    <div className={`call-bar surface-line call-bar-${call.state}${incoming ? ' call-bar-incoming' : ''}`} role="region" aria-label="Current call">
      <span className={`dot dot-${lamp}`} aria-hidden="true" />
      <Icon icon={PhoneCall} />
      <span className="call-bar-name">{incoming ? `Incoming: ${name}` : name}</span>
      {incoming && !ws.session.view_as ? (
        <span className="call-bar-actions">
          {canDecline ? (
            <button type="button" className="btn btn-small btn-end" onClick={() => void ws.callControl.act(call, call.allowed_actions.includes('reject') ? 'reject' : 'hangup')}>
              Decline
            </button>
          ) : null}
          <button type="button" className="btn btn-small btn-live" disabled={!canAnswer} onClick={() => { armCallWindowAnswer(ws, call.id); void ws.callControl.act(call, 'answer'); }}>
            Answer
          </button>
        </span>
      ) : null}
      <Link to="/phone" className={`btn btn-small${incoming ? '' : ''}`}>Open phone</Link>
    </div>
  );
}

function StatusBar() {
  const ws = useWorkspace();
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (k: string) => setOpen(o => (o === k ? null : k));
  const s = ws.stream;
  // Catching up after a reconnect (and re-checking media access) is a background refresh: the masthead bar
  // shows it, and this value keeps one width (styles/refresh.css) so the status bar never shifts.
  useBackgroundRefresh(s.state === 'resyncing' || ws.mediaAccess.state === 'refreshing');
  const updates = s.state === 'live' ? 'Live' : s.state === 'resyncing' ? 'Refreshing' : s.state === 'reconnecting' ? 'Reconnecting' : s.state === 'offline' ? 'Offline' : s.state === 'unauthenticated' ? 'Signed out' : s.state === 'connecting' || s.state === 'idle' ? 'Connecting' : 'Stopped';
  const updatesTone = s.state === 'live' ? 'live' : s.state === 'offline' || s.state === 'unauthenticated' ? 'end' : 'warn';
  const trCap = capability(ws.config.value, 'transcription.live');
  const tr = gate(trCap);
  const trText = gateShortLabel(tr);
  const cfg = ws.config.value;
  const phoneTone = readinessTone(ws.readiness.state);
  return (
    <footer className="statusbar surface-line" aria-label="System status">
      <StatusItem id="st-phone" label="Phone" value={ws.readiness.label} tone={phoneTone} open={open === 'phone'} onToggle={() => toggle('phone')}>
        <p>{ws.readiness.reason}</p>
        {ws.readiness.action === 'retry' ? <button type="button" className="btn btn-small" onClick={ws.phone.retry}>Retry now</button> : null}
        <Diagnostics diagnostic={ws.readiness.diagnostic ?? (ws.device ? { code: null, detail: `Device ${ws.device.label}, microphone ${ws.mic}` } : null)} />
      </StatusItem>
      <StatusItem id="st-updates" className="status-item-updates" label="Updates" value={updates} tone={updatesTone} open={open === 'updates'} onToggle={() => toggle('updates')}>
        <p>{s.lastEventAt ? `Last event at ${formatClock(s.lastEventAt)}.` : 'No events received yet.'}{s.nextRetryAt ? ` Next attempt in ${formatDuration(Math.max(0, s.nextRetryAt - Date.now()))}.` : ''}{s.attempt > 0 ? ` Attempt ${s.attempt}.` : ''}</p>
        {s.state !== 'live' && s.state !== 'unauthenticated' ? <button type="button" className="btn btn-small" onClick={ws.reconnectStream}>Reconnect now</button> : null}
      </StatusItem>
      <StatusItem id="st-tr" label="Transcription" value={trText} tone={tr.render === 'normal' ? 'live' : 'warn'} open={open === 'tr'} onToggle={() => toggle('tr')}>
        <p>{featureNotice('transcription', tr) ?? 'Transcription is available. Your calls aren’t affected if it stops.'}</p>
        <Diagnostics diagnostic={gateDiagnostic(tr, trCap?.reason_code ?? null)} />
      </StatusItem>
      {cfg && (cfg.environment !== 'production' || cfg.data_mode === 'fixture') ? (
        <StatusItem id="st-env" label="Environment" value={cfg.data_mode === 'fixture' ? 'Fixture data' : cfg.environment === 'staging' ? 'Staging' : 'Development'} tone="warn" open={open === 'env'} onToggle={() => toggle('env')}>
          <p className="mono">Build {cfg.build_id}, API v{cfg.api_version}</p>
        </StatusItem>
      ) : null}
      {ws.config.error ? <span className="status-item tone-end">Settings unavailable: {ws.config.error.title}</span> : null}
    </footer>
  );
}

function StatusItem({ id, className, label, value, tone, open, onToggle, children }: { id: string; className?: string; label: string; value: string; tone: string; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <div className="status-wrap">
      <button type="button" className={`status-item tone-${tone}${className ? ` ${className}` : ''}`} aria-expanded={open} aria-controls={id} onClick={onToggle}>
        <span className={`dot dot-${tone}`} aria-hidden="true" />
        <span className="status-label">{label}</span> <span className="status-value">{value}</span>
      </button>
      {open ? <div id={id} className="popover status-pop" role="region" aria-label={`${label} details`}>{children}</div> : null}
    </div>
  );
}

function useWindowTitle(section: string, live: import('@bcio-voice/contracts').Call | null, incomingCaller: string | null = null) {
  useEffect(() => {
    if (incomingCaller) {
      let flip = false;
      document.title = `INCOMING CALL: ${incomingCaller} - ${windowTitle(section)}`;
      const t = setInterval(() => {
        flip = !flip;
        document.title = flip ? windowTitle(`Incoming: ${incomingCaller}`) : `INCOMING CALL: ${incomingCaller}`;
      }, 1000);
      return () => clearInterval(t);
    }
    document.title = live && live.state === 'active' ? windowTitle('On call') : windowTitle(section);
    return undefined;
  }, [section, live?.state, incomingCaller]);
}

/** F6 / Shift+F6 cycle focus between the landmarks. */
function useRegionCycling() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'F6') return;
      const regions = ['.topbar', '#main', '#phone', '.statusbar', '.bottom-nav'].map(s => document.querySelector<HTMLElement>(s)).filter((el): el is HTMLElement => !!el && el.offsetParent !== null);
      if (!regions.length) return;
      e.preventDefault();
      const current = regions.findIndex(r => r.contains(document.activeElement));
      const next = regions[(current + (e.shiftKey ? -1 : 1) + regions.length) % regions.length];
      if (!next) return;
      if (!next.hasAttribute('tabindex')) next.tabIndex = -1;
      next.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export { ViewAsBanner, ViewAsMenuSection };
